import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DancerRecitalPackage,
  DancerRecitalPackageInput,
  DancerRecitalPackageStatusUpdate,
  RecitalPackage,
  RecitalPackageInput,
} from '@pirouette/shared';
import {
  dancerRecitalPackageInputSchema,
  dancerRecitalPackageStatusUpdateSchema,
  recitalPackageInputSchema,
} from '@pirouette/shared';
import { stripUndefined } from '@/lib/db';
import { supabase } from '@/lib/supabase';

export type RecitalPackageWithProduction = RecitalPackage & {
  productions?: { id: string; name: string; season: string } | null;
};

export type DancerRecitalPackageWithDetails = DancerRecitalPackage & {
  dancers?: {
    id: string;
    first_name: string;
    last_name: string;
    preferred_name: string | null;
  } | null;
  recital_packages?: RecitalPackage | null;
};

export const packageItemStatusOptions = [
  'not_started',
  'ordered',
  'received',
  'delivered',
  'waived',
] as const;

export function useRecitalPackages(productionId?: string) {
  return useQuery({
    queryKey: ['recital-packages', productionId ?? 'all'],
    queryFn: async (): Promise<RecitalPackageWithProduction[]> => {
      let query = supabase
        .from('recital_packages')
        .select('*, productions(id, name, season)')
        .order('created_at', { ascending: false });

      if (productionId) query = query.eq('production_id', productionId);

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useDancerRecitalPackages(dancerId?: string) {
  return useQuery({
    queryKey: ['dancer-recital-packages', dancerId ?? 'all'],
    queryFn: async (): Promise<DancerRecitalPackageWithDetails[]> => {
      let query = supabase
        .from('dancer_recital_packages')
        .select('*, dancers(id, first_name, last_name, preferred_name), recital_packages(*)')
        .order('created_at', { ascending: false });

      if (dancerId) query = query.eq('dancer_id', dancerId);

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateRecitalPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecitalPackageInput): Promise<RecitalPackage> => {
      const parsed = recitalPackageInputSchema.parse(input);
      const { data, error } = await supabase
        .from('recital_packages')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (pkg) => {
      void qc.invalidateQueries({ queryKey: ['recital-packages'] });
      void qc.invalidateQueries({ queryKey: ['recital-packages', pkg.production_id] });
    },
  });
}

export function useAssignRecitalPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DancerRecitalPackageInput): Promise<DancerRecitalPackage> => {
      const parsed = dancerRecitalPackageInputSchema.parse(input);
      const { data, error } = await supabase
        .from('dancer_recital_packages')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (assignment) => {
      void qc.invalidateQueries({ queryKey: ['dancer-recital-packages'] });
      void qc.invalidateQueries({
        queryKey: ['dancer-recital-packages', assignment.dancer_id],
      });
    },
  });
}

export function useUpdateDancerRecitalPackageStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DancerRecitalPackageStatusUpdate): Promise<DancerRecitalPackage> => {
      const { id, ...rest } = dancerRecitalPackageStatusUpdateSchema.parse(input);
      const { data, error } = await supabase
        .from('dancer_recital_packages')
        .update(stripUndefined(rest))
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (assignment) => {
      void qc.invalidateQueries({ queryKey: ['dancer-recital-packages'] });
      void qc.invalidateQueries({
        queryKey: ['dancer-recital-packages', assignment.dancer_id],
      });
    },
  });
}

export function useCreateRecitalPackageCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dancerPackageId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('create_recital_package_charge', {
        p_dancer_package_id: dancerPackageId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dancer-recital-packages'] });
      void qc.invalidateQueries({ queryKey: ['charges'] });
      void qc.invalidateQueries({ queryKey: ['family-balances'] });
      void qc.invalidateQueries({ queryKey: ['family-balance'] });
      void qc.invalidateQueries({ queryKey: ['account-balances'] });
    },
  });
}
